/**
 * Main payment action handler that routes to specific action handlers
 */
import { useCallback, useRef } from 'react';
import { useThreedsAuthAction } from './useThreedsAuthAction';
import { useProcessorAuthAction } from './useProcessorAuthAction';
import { useRedirectAction } from './useRedirectAction';
import { useErrorAction } from './useErrorAction';
import { useFinixRadarAction } from './useFinixRadarAction';
import { useAirwallexRadarAction } from './useAirwallexRadarAction';
import { useStripeRadarAction } from './useStripeRadarAction';
import { useKessPayAction } from './useKessPayAction';
import { useTrustFlowAction } from './useTrustFlowAction';
import { useMasterCardAction } from './useMasterCardAction';
import { useNgeniusThreedsAction } from './useNgeniusThreedsAction';
export function usePaymentActionHandler({ paymentsResource, startChallenge, startPolling, setIsLoading, setError, hookOptionsRef, }) {
    // Track challenge in progress to prevent multiple challenges
    const challengeInProgressRef = useRef(false);
    // Initialize all action handlers
    const { handleThreedsAuth } = useThreedsAuthAction({
        startChallenge,
        startPolling,
        setIsLoading,
        setError,
        hookOptionsRef,
        challengeInProgressRef,
    });
    const { handleProcessorAuth } = useProcessorAuthAction();
    const { handleRedirect } = useRedirectAction({
        setIsLoading,
        hookOptionsRef,
    });
    const { handleError } = useErrorAction({
        setError,
        setIsLoading,
    });
    const { handleFinixRadar } = useFinixRadarAction({
        paymentsResource,
        startPolling,
        setError,
        setIsLoading,
        hookOptionsRef,
    });
    const { handleAirwallexRadar } = useAirwallexRadarAction({
        paymentsResource,
        startPolling,
        setError,
        setIsLoading,
        hookOptionsRef,
    });
    const { handleStripeRadar } = useStripeRadarAction({
        paymentsResource,
        startPolling,
        setError,
        setIsLoading,
        hookOptionsRef,
    });
    const { handleKessPayAuth } = useKessPayAction({
        setError,
        setIsLoading,
        hookOptionsRef,
    });
    const { handleTrustFlowAuth } = useTrustFlowAction({
        setError,
        setIsLoading,
        hookOptionsRef,
    });
    const { handleMasterCardAuth } = useMasterCardAction({
        paymentsResource,
        startPolling,
        setError,
        setIsLoading,
        hookOptionsRef,
    });
    const { handleNgeniusThreeds } = useNgeniusThreedsAction({
        paymentsResource,
        startPolling,
        setError,
        setIsLoading,
        hookOptionsRef,
    });
    // Main handler that routes to specific action handlers
    const handlePaymentAction = useCallback(async (payment, options = {}) => {
        if (payment.requireAction === 'none')
            return;
        if (payment?.requireActionData?.processed)
            return;
        const actionData = payment.requireActionData;
        if (!actionData)
            return;
        // Mark action as processed
        try {
            await paymentsResource.markPaymentActionProcessed(payment.id);
        }
        catch (_error) {
            // Error handling removed
        }
        // Route to appropriate action handler based on type
        switch (actionData.type) {
            case 'threeds_auth':
                await handleThreedsAuth(payment, actionData, options);
                break;
            case 'processor_auth':
            case 'redirect_to_payment':
                await handleProcessorAuth(actionData);
                break;
            case 'redirect':
                await handleRedirect(payment, actionData, options);
                break;
            case 'error':
                await handleError(payment, actionData, options);
                break;
            case 'finix_radar':
                await handleFinixRadar(payment, actionData, options, handlePaymentAction);
                break;
            case 'stripe_radar':
                await handleStripeRadar(payment, actionData, options, handlePaymentAction);
                break;
            case 'kesspay_auth':
                await handleKessPayAuth(payment, actionData, options);
                break;
            case 'trustflow_auth':
                await handleTrustFlowAuth(payment, actionData, options);
                break;
            case 'mastercard_auth':
                await handleMasterCardAuth(payment, actionData, options, handlePaymentAction);
                break;
            case 'radar':
                // Check provider for specific implementation
                if (actionData.metadata?.provider === 'airwallex') {
                    await handleAirwallexRadar(payment, actionData, options, handlePaymentAction);
                }
                break;
            case 'ngenius_3ds':
                await handleNgeniusThreeds(payment, actionData, options);
                break;
        }
        options.onRequireAction?.(payment);
    }, [
        paymentsResource,
        handleThreedsAuth,
        handleProcessorAuth,
        handleRedirect,
        handleError,
        handleFinixRadar,
        handleAirwallexRadar,
        handleStripeRadar,
        handleKessPayAuth,
        handleTrustFlowAuth,
        handleMasterCardAuth,
        handleNgeniusThreeds,
    ]);
    return { handlePaymentAction };
}
